import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ServerPagination } from "@/components/ui/server-pagination";

const actionColors: Record<string, string> = {
  create: "bg-primary/10 text-primary",
  update: "bg-accent-orange/10 text-accent-orange",
  delete: "bg-destructive/10 text-destructive",
  approve: "bg-primary/10 text-primary",
  reject: "bg-destructive/10 text-destructive",
};

const AuditLog = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tableFilter, setTableFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setCurrentPage(1); }, [tableFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ["audit-logs", tableFilter, debouncedSearch, currentPage, pageSize],
    queryFn: async () => {
      let q = supabase
        .from("audit_logs")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false });
      if (tableFilter !== "all") q = q.eq("table_name", tableFilter);
      if (debouncedSearch) {
        const s = `%${debouncedSearch}%`;
        q = q.or(`action.ilike.${s},table_name.ilike.${s}`);
      }
      const from = (currentPage - 1) * pageSize;
      q = q.range(from, from + pageSize - 1);
      const { data, error, count } = await q;
      if (error) throw error;
      return { logs: data ?? [], totalCount: count ?? 0 };
    },
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ["audit-profiles"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("user_id, full_name");
      return data ?? [];
    },
  });

  const logs = data?.logs ?? [];
  const totalCount = data?.totalCount ?? 0;

  const getUserName = (userId: string | null) => {
    if (!userId) return "System";
    const p = profiles.find((pr: any) => pr.user_id === userId);
    return p?.full_name || userId.slice(0, 8);
  };

  const tables = ["all", "students", "applications", "invoices", "payments", "exam_results", "registrations", "discipline_cases", "service_requests"];

  const timeAgo = (t: string) => {
    const diff = Date.now() - new Date(t).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(t).toLocaleDateString();
  };

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Audit Log</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Track all changes and actions across the system</p>
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search logs..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={tableFilter} onValueChange={setTableFilter}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {tables.map((t) => (
                <SelectItem key={t} value={t}>{t === "all" ? "All tables" : t.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>{totalCount} entries</CardDescription>
          </CardHeader>
          <CardContent className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Table</TableHead>
                  <TableHead>Record</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log: any) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{timeAgo(log.created_at)}</TableCell>
                    <TableCell className="font-medium">{getUserName(log.user_id)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={actionColors[log.action] || ""}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{log.table_name.replace("_", " ")}</TableCell>
                    <TableCell className="font-mono text-xs">{log.record_id?.slice(0, 8) || "—"}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
                      {log.action === "create" && log.new_data && JSON.stringify(log.new_data).slice(0, 80)}
                      {log.action === "update" && "Updated fields"}
                      {log.action === "delete" && "Record deleted"}
                    </TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      {isLoading ? "Loading..." : "No audit entries found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ServerPagination currentPage={currentPage} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default AuditLog;
